/* EXOS collection chooser — tag filtering for the collection grid, no reload.
   Fetches the theme's own collection section re-rendered under a tag URL
   (Section Rendering API) and swaps [data-collection-products] in place with
   a fade-out / height-tween / staggered fade-in transition.
   URL state lives in the ?doors= query parameter so views are shareable
   and back/forward works; the canonical URL is untouched.
   2026-09-23: the section can name another grid to filter (data-grid-target, used by the
   EXOS grouped grid on Trim), the status noun (data-noun-singular/plural) and keeps a ?view=
   template parameter on section fetches. Defaults reproduce the original Doors behaviour. */
(function () {
  'use strict';

  var PARAM_DEFAULT = 'doors';
  var GRID_ITEM_SELECTOR = '.product-grid-item';
  var SWAP_OUT_MS = 180;
  var SWAP_IN_MS = 380;

  function initAll() {
    document.querySelectorAll('[data-exos-chooser]').forEach(function (root) {
      if (root.exosChooserInit) return;
      root.exosChooserInit = true;
      new Chooser(root);
    });
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function Chooser(root) {
    this.root = root;
    this.param = root.dataset.paramName || PARAM_DEFAULT;
    this.collectionUrl = root.dataset.collectionUrl || window.location.pathname;
    var gridTarget = (root.dataset.gridTarget || '').trim();
    this.mainSection =
      (gridTarget && document.querySelector(gridTarget)) ||
      document.querySelector('[data-section-type="collection-template"]');
    this.nounOne = root.dataset.nounSingular || 'door';
    this.nounMany = root.dataset.nounPlural || 'doors';
    this.grid = this.mainSection && this.mainSection.querySelector('[data-collection-products]');
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-chooser-tag]'));
    this.clearBtn = root.querySelector('[data-chooser-clear]');
    this.status = root.querySelector('[data-chooser-status]');
    this.emptyMessage = (this.status && this.status.dataset.emptyMessage) || '';
    this.defaultTag = (root.dataset.defaultTag || '').trim().toLowerCase();
    this.busy = false;
    this.activeTag = '';
    this.cleanupTimer = null;
    this.pending = null;

    if (!this.grid || !this.cards.length) return;
    this.sectionId = this.mainSection.dataset.sectionId;

    this.bind();
    this.applyFromLocation(false);
  }

  Chooser.prototype.bind = function () {
    var self = this;

    this.cards.forEach(function (card) {
      card.addEventListener('click', function () {
        var tag = card.dataset.chooserTag;
        if (!tag) return;
        var next = self.activeTag === tag ? (self.defaultTag || '') : tag;
        self.setFilter(next, { push: true, scroll: next !== '' });
      });
    });

    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', function () {
        self.setFilter(self.defaultTag || '', { push: true, scroll: false });
      });
    }

    // Keep pagination working while filtered: re-fetch instead of navigating
    // to the /collections/x/tag URL the server renders inside the fragment.
    this.grid.addEventListener('click', function (event) {
      if (!self.activeTag) return;
      var link = event.target.closest('.pagination a, a[href*="page="]');
      if (!link || !self.grid.contains(link)) return;
      var url;
      try {
        url = new URL(link.href, window.location.origin);
      } catch (error) {
        return;
      }
      var page = url.searchParams.get('page') || '';
      event.preventDefault();
      self.setFilter(self.activeTag, { push: true, scroll: true, page: page });
    });

    window.addEventListener('popstate', function () {
      self.applyFromLocation(false);
    });
  };

  Chooser.prototype.applyFromLocation = function (scroll) {
    var params = new URLSearchParams(window.location.search);
    // Lowercase so a hand-edited ?doors=Prehung still matches the canonical
    // tag URL instead of bouncing through Shopify's 301 case normalization.
    var tag = (params.get(this.param) || '').trim().toLowerCase();
    var page = params.get('page') || '';
    var isDefault = false;

    // A tag with no matching card (for example an old ?doors=prehung link after that card was
    // removed) falls back to the default view instead of fetching an empty grid.
    if (tag && this.cards.length && !this.cards.some(function (card) {
      return (card.getAttribute('data-chooser-tag') || '').toLowerCase() === tag;
    })) {
      tag = this.defaultTag || '';
      isDefault = !!tag;
    }

    // No parameter means the default view: show only the default tag.
    if (!tag && this.defaultTag) {
      tag = this.defaultTag;
      isDefault = true;
    }

    if (!tag && !this.activeTag) {
      this.reveal();
      this.reflectState(0, null);
      return;
    }
    if (tag === this.activeTag && isDefault) {
      this.reveal();
      return;
    }
    this.setFilter(tag, { push: false, scroll: scroll, page: page, initial: isDefault });
  };

  Chooser.prototype.reveal = function () {
    document.documentElement.classList.remove('exos-awaiting-default');
  };

  Chooser.prototype.fetchUrl = function (tag, page) {
    var base = this.collectionUrl.replace(/\/$/, '');
    var path = tag ? base + '/' + encodeURIComponent(tag) : base;
    var params = new URLSearchParams();
    params.set('section_id', this.sectionId);
    var current = new URLSearchParams(window.location.search);
    if (current.get('sort_by')) params.set('sort_by', current.get('sort_by'));
    // Alternate templates (?view=trim) must be carried, or the section id will not resolve.
    if (current.get('view')) params.set('view', current.get('view'));
    if (page) params.set('page', page);
    return path + '?' + params.toString();
  };

  Chooser.prototype.pageUrl = function (tag, page) {
    var params = new URLSearchParams(window.location.search);
    params.delete(this.param);
    params.delete('page');
    if (tag && tag !== this.defaultTag) params.set(this.param, tag);
    if (tag && page && page !== '1') params.set('page', page);
    var query = params.toString();
    return window.location.pathname + (query ? '?' + query : '');
  };

  Chooser.prototype.setFilter = function (tag, options) {
    var self = this;
    options = options || {};
    if (this.busy) {
      // Don't drop a click (or Back/Forward) that lands mid-swap —
      // remember the latest request and apply it when this swap settles.
      this.pending = { tag: tag, options: options };
      return;
    }
    this.busy = true;

    var reduceMotion = prefersReducedMotion();
    var swapDelay = reduceMotion ? 0 : SWAP_OUT_MS;
    var started = Date.now();

    // If a previous swap is still animating in, settle it before starting over.
    if (this.cleanupTimer) {
      window.clearTimeout(this.cleanupTimer);
      this.cleanupTimer = null;
      this.grid.classList.remove('exos-swap-in');
    }

    this.grid.classList.add('exos-chooser-loading');
    if (!reduceMotion) {
      // Freeze the current height so it can tween to the new content's
      // height, and fade the old products out while the fragment loads.
      this.grid.style.height = this.grid.offsetHeight + 'px';
      this.grid.style.overflow = 'hidden';
      this.grid.classList.add('exos-swap-out');
    }

    // Start moving right away so the click feels instant.
    if (options.scroll) this.scrollToGrid();

    var wasRedirected = false;

    fetch(this.fetchUrl(tag, options.page), { headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        // Shopify 301-redirects /collections/x/<tag> back to /collections/x
        // when no published product carries the tag — that means 0 results,
        // not "show everything".
        wasRedirected = response.redirected;
        return response.text();
      })
      .then(function (html) {
        var commit;

        if (tag && wasRedirected) {
          commit = function () {
            var message = self.emptyMessage || 'No doors match this option yet.';
            self.grid.innerHTML =
              '<div class="collection--no-results"><p class="no-matches">' +
              message +
              '</p></div>';
            return 0;
          };
        } else {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-collection-products]');
          if (!fresh) throw new Error('Fragment missing product grid');

          fresh.querySelectorAll('[data-aos]').forEach(function (el) {
            el.removeAttribute('data-aos');
            el.removeAttribute('data-aos-anchor');
            el.removeAttribute('data-aos-delay');
          });

          var count = fresh.querySelectorAll(GRID_ITEM_SELECTOR).length;
          commit = function () {
            self.grid.innerHTML = fresh.innerHTML;
            if (tag && count === 0 && self.emptyMessage) {
              var noMatches = self.grid.querySelector('.no-matches');
              if (noMatches) noMatches.textContent = self.emptyMessage;
            }
            return count;
          };
        }

        // Let the fade-out finish before swapping the content in.
        var wait = Math.max(0, swapDelay - (Date.now() - started));
        window.setTimeout(function () {
          var count = commit();
          self.activeTag = tag;

          if (options.push) {
            window.history.pushState({ exosChooser: tag }, '', self.pageUrl(tag, options.page));
          }

          self.reflectState(count, tag);
          self.animateIn(reduceMotion);
          self.reveal();
          self.busy = false;

          document.dispatchEvent(
            new CustomEvent('exos:chooser:filtered', { detail: { tag: tag, count: count } })
          );

          var next = self.pending;
          self.pending = null;
          if (next && (next.tag !== tag || (next.options.page || '') !== (options.page || ''))) {
            self.setFilter(next.tag, next.options);
          }
        }, wait);
      })
      .catch(function () {
        self.busy = false;
        self.grid.classList.remove('exos-swap-out', 'exos-chooser-loading');
        self.grid.style.height = '';
        self.grid.style.overflow = '';
        self.reveal();
        // On the initial default pass, fall back to the already-rendered
        // full grid rather than navigating the customer somewhere else.
        if (options.initial) return;
        // Progressive fallback: let the server render the tag-filtered page.
        if (tag) {
          window.location.href =
            self.collectionUrl.replace(/\/$/, '') + '/' + encodeURIComponent(tag);
        } else {
          window.location.href = self.collectionUrl;
        }
      });
  };

  Chooser.prototype.animateIn = function (reduceMotion) {
    var grid = this.grid;
    var self = this;

    if (reduceMotion) {
      grid.classList.remove('exos-swap-out', 'exos-chooser-loading');
      grid.style.height = '';
      grid.style.overflow = '';
      return;
    }

    var items = grid.querySelectorAll(GRID_ITEM_SELECTOR);
    items.forEach(function (el, index) {
      el.style.setProperty('--exos-i', Math.min(index, 11));
    });

    grid.classList.remove('exos-swap-out');
    grid.classList.add('exos-swap-in');
    grid.style.height = grid.scrollHeight + 'px';

    // Wait for the slowest staggered tile (30ms/tile, capped at 11) so late
    // tiles finish their fade instead of snapping when the class is removed.
    var settleDelay = SWAP_IN_MS + Math.min(items.length, 12) * 30;

    this.cleanupTimer = window.setTimeout(function () {
      grid.style.height = '';
      grid.style.overflow = '';
      grid.classList.remove('exos-swap-in', 'exos-chooser-loading');
      self.cleanupTimer = null;
    }, settleDelay);
  };

  Chooser.prototype.scrollToGrid = function () {
    this.mainSection.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start'
    });
  };

  Chooser.prototype.reflectState = function (count, tag) {
    var activeLabel = '';

    this.cards.forEach(function (card) {
      var isActive = !!tag && card.dataset.chooserTag === tag;
      card.classList.toggle('is-active', isActive);
      card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      if (isActive) {
        var labelEl = card.querySelector('.exos-chooser__label');
        activeLabel = labelEl ? labelEl.textContent.trim() : tag;
      }
    });

    var atDefault = !tag || (this.defaultTag && tag === this.defaultTag);
    if (this.clearBtn) this.clearBtn.hidden = !!atDefault;

    if (this.status) {
      if (!tag) {
        this.status.textContent = '';
      } else if (count === 0) {
        this.status.textContent = this.emptyMessage || 'No doors match this option yet.';
      } else {
        this.status.textContent =
          count + ' ' + (count === 1 ? this.nounOne : this.nounMany) + ' shown for ' + activeLabel + '.';
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
